'use client'
import { CardSize, IContent } from '../../../../utils/type'
import { CSSProperties, ReactNode, useMemo } from 'react'
import { CardText } from './CardText'
import { CardImage } from './CardImage'
import { CardVideo } from './CardVideo'
import { CardAudio } from './CardAudio'
import { CardHtml } from './CardHtml'
import { useRequest } from 'ahooks'
import { DisplayType } from '../../../../utils/http/Services/market'
import styled from 'styled-components'
import { CardRecursive } from './CardRecursive'
import { fetchText } from '../../../../utils'

export default function InscriptionCard(props: {
  content: IContent
  size?: CardSize
  children?: ReactNode
  className?: string
  style?: CSSProperties
}) {
  const size = props.size || CardSize.Medium
  const { _content_uri, _content_type } = useContent(props.content)
  const displayType = props.content.display_type
  const MediaComponent = useMemo(() => {
    if (_content_type.startsWith('text/html')) return CardHtml
    if (_content_type.startsWith('text')) return CardText
    if (_content_type.startsWith('video')) return CardVideo
    if (_content_type.startsWith('audio')) return CardAudio
    if (displayType === DisplayType.recursive) return CardRecursive
    if (displayType === DisplayType.bitmap) {
      return FilteredCardImage
    }
    return CardImage
  }, [_content_type, displayType])
  return (
    <MediaComponent size={size} style={props.style} content_uri={_content_uri} className={props.className}>
      {props.children}
    </MediaComponent>
  )
}

function useContent(content: IContent) {
  const { content_type = '', content_uri, display_type } = content
  const { json } = useJSONContent(content_uri, display_type)

  const { _content_type, _content_uri } = useMemo(() => {
    let _content_uri = content_uri
    let _content_type = content_type
    if (display_type === DisplayType.btcdomain && json.img_url) {
      _content_uri = json.img_url
      _content_type = 'image'
    }

    if (display_type === DisplayType.bitmap) {
      const match = content_uri.match(/\w+$/)
      if (match) {
        const inscriptionId = match[0]
        _content_uri = `https://preview.ordswap.io/bitmap/${inscriptionId}`
        _content_type = 'image'
      }
    }
    return { _content_uri, _content_type }
  }, [content_uri, display_type, content_type, json])

  return { _content_uri, _content_type }
}

function useJSONContent(uri: string, type?: DisplayType) {
  const { data, loading } = useRequest(() => fetchText(uri), {
    refreshDeps: [uri],
    cacheKey: uri,
    ready: !!type && [DisplayType.bitmap].includes(type),
  })
  const json = useMemo(() => {
    const _data = data || ''
    try {
      const _json = JSON.parse(_data)
      return _json
    } catch (e) {
      return {}
    }
  }, [data])
  return { json, loading }
}

const FilteredCardImage = styled(CardImage)`
  img.cover {
    filter: grayscale(1) invert(1) sepia(1) saturate(20) hue-rotate(346deg);
  }
`
